import React from 'react'
import {Util, UUID} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {UiSlotName} from '@api/components/ui-slot'
import {Sync} from '../../client/sync'
import {globals} from '../../extensions/globals-base'
import {ApiUiCategory, UIRenderer} from './ui'
import {UIElement} from './ui-element'

type ListenerKey = DataObjectId | ApiUiCategory
type ExtensionKey = string | number

export interface Renderer {
  key: string
  renderer: React.ComponentType<{data: any}>
}

type RenderersSetter = (renderers: Renderer[]) => void

export type Disposer = () => void

export class UIRegistry {
  public static all: Map<DataObjectId, UIRegistry> = new Map()
  /**
   * Currently registered react slots. Key is slot name, value is an array of setters that will be called when slot content changes.
   */
  public static reactSlots: Map<UiSlotName, RenderersSetter[]> = new Map()
  public static reactInjections: Map<UiSlotName, Renderer[]> = new Map()

  public static get(companyId: DataObjectId): UIRegistry {
    if (!companyId || !Sync.get({companyId})) {
      throw new Error(`Company not found ${companyId}`)
    }
    const found = UIRegistry.all.get(companyId)
    if (found) {
      return found
    } else {
      const res = new UIRegistry(companyId)
      UIRegistry.all.set(companyId, res)
      return res
    }
  }

  public static init(): void {
    globals.onCompanyChange(() => {
      UIRegistry.all.clear()
    })
  }

  public companyId: DataObjectId

  constructor(companyId: DataObjectId) {
    this.companyId = companyId
  }

  /**
   * Map containing ui root element objects for different Entities
   * that have customized ui.
   */
  public uiRegistry: Map<string, UIElement> = new Map()
  uiRenderers: Map<string, UIRenderer> = new Map()
  extensionRenderers: Map<ExtensionKey, ApiUiCategory[]> = new Map()

  private registryListeners: Map<ListenerKey, Map<UUID, () => void>> = new Map()
  private registryListenerHandlers: Map<UUID, ListenerKey> = new Map()

  public addListener(id: ListenerKey, cb: () => void): UUID {
    const handler = Util.UUID()
    this.registryListenerHandlers.set(handler, id)
    const listeners = this.registryListeners.get(id)
    if (listeners) {
      listeners.set(handler, cb)
    } else {
      const newListeners = new Map()
      newListeners.set(handler, cb)
      this.registryListeners.set(id, newListeners)
    }
    return handler
  }

  public removeListener(handler: UUID): void {
    const id = this.registryListenerHandlers.get(handler)
    if (!id) {
      return
    }
    const m = this.registryListeners.get(id)
    if (m) {
      m.delete(handler)
    }
    this.registryListenerHandlers.delete(handler)
  }

  public refresh(id: ListenerKey): void {
    const m = this.registryListeners.get(id)
    if (m) {
      m.forEach((cb: () => void) => cb())
    }
  }

  public get(id: string): UIElement | undefined {
    return this.uiRegistry.get(id)
  }

  public addExtensionCategory(
    extension: ExtensionKey,
    category: ApiUiCategory
  ): void {
    const extRenderers = this.extensionRenderers
    const categories = extRenderers.get(extension)
    extRenderers.set(
      extension,
      categories ? [...categories, category] : [category]
    )
  }

  public removeExtensionCategory(
    extension: ExtensionKey,
    category: ApiUiCategory
  ): void {
    const extRenderers = this.extensionRenderers
    const categories = extRenderers.get(extension)
    if (categories) {
      const newCategories = categories.filter(c => c !== category)
      extRenderers.set(extension, newCategories)
    }
  }

  public clearExtensionRenderers(extension: ExtensionKey): void {
    const renderersKeys = this.extensionRenderers.get(extension)
    if (renderersKeys?.length) {
      for (const rendererKey of renderersKeys) {
        // clearing prev renderers and trigger refresh
        this.uiRenderers.set(rendererKey, () => undefined)
        this.refresh(rendererKey)
        // clearing subscriptions
        this.uiRenderers.delete(rendererKey)
      }
      this.extensionRenderers.delete(extension)
    }
  }

  /**
   * @experimental
   * Register a slot for react components. This method is used internally by Slot component.
   * @param slotName Unique slot name
   * @param setter Function that will be called with an array of React components that should be rendered in the slot
   */
  public static registerSlot(
    slotName: UiSlotName,
    setter: RenderersSetter
  ): Disposer {
    if (!UIRegistry.reactSlots.has(slotName)) {
      UIRegistry.reactSlots.set(slotName, [])
    }
    UIRegistry.reactSlots.get(slotName)?.push(setter)

    // Apply slot
    setter(UIRegistry.reactInjections.get(slotName) || [])

    return () => {
      const arr = UIRegistry.reactSlots.get(slotName)
      if (arr) {
        const idx = arr.indexOf(setter)
        if (idx >= 0) {
          arr.splice(idx, 1)
        }
      }
    }
  }

  /**
   * @experimental
   * Register a slotName injection. This method is used internally by UI.embedComponentToSlot.
   * @param slotName Unique slotName name
   * @param key Unique key for the injection
   * @param component React component to render in the slotName
   */
  public static internalRegisterSlotInjection(
    slotName: UiSlotName,
    key: string,
    component: React.ComponentType<{data: any}>
  ): Disposer {
    if (!UIRegistry.reactInjections.has(slotName)) {
      UIRegistry.reactInjections.set(slotName, [])
    }
    const arr = UIRegistry.reactInjections.get(slotName)
    UIRegistry.reactInjections.set(slotName, [
      ...(arr || []),
      {key, renderer: component}
    ])

    // Refresh all slots that use this injection
    UIRegistry.refreshSlotInjections(slotName)

    return () => {
      const arr = UIRegistry.reactInjections.get(slotName)
      if (arr) {
        UIRegistry.reactInjections.set(
          slotName,
          arr.filter(r => r.key !== key)
        )
        UIRegistry.refreshSlotInjections(slotName)
      }
    }
  }

  /**
   * @experimental
   * Refresh all slots that use the given injection.
   * @param slotName
   * @private
   */
  private static refreshSlotInjections(slotName: UiSlotName): void {
    const setters = UIRegistry.reactSlots.get(slotName)
    if (setters) {
      for (const setter of setters) {
        setter(UIRegistry.reactInjections.get(slotName) || [])
      }
    }
  }
}
